import { useEffect } from 'react';

import { useSoyuzData } from '@spotahome/soyuz/client';
import Metrics from '@spotahome/soyuz-tracking';

import {
  ThemeProvider,
  CookiesProvider,
  ToastContainer,
  GoogleOneTap,
  UserInfoProvider,
  HotspotsProvider,
  CitiesProvider,
  DeviceProvider,
  FiltersProvider,
  FacetSearchProvider,
  LoginSignupModalWrapper
} from '@spotahome/ui-library';
import useCoreWebVitals from '@spotahome/ui-library/src/hooks/useCoreWebVitals';
import usePageLoadTime from '@spotahome/ui-library/src/hooks/usePageLoadTime';
import ZohoChatbot from '@spotahome/ui-library/src/tenant/components/ZohoChatbot/ZohoChatbot';

import { configureMetricsActions } from '@spotahome/seo-landings-common/src/common/graphql/mutations/tenantActions';
import { loadModules } from '@spotahome/seo-landings-common/src/configurator';
import OptInModalWrapper from '@spotahome/seo-landings-common/src/components/OptInModalWrapper';
import SeoLocalesContainer from '@spotahome/seo-landings-common/src/components/SeoLocalesContainer';
import { SeoCitiesProvider } from '@spotahome/seo-landings-common/src/context/SeoCitiesContext';
import SeoPageWrapper from '@spotahome/seo-landings-common/src/components/SeoPageWrapper';
import NotFound from '@spotahome/seo-landings-common/src/components/NotFound';
import SeoSearchProvider from '@spotahome/seo-landings-common/src/context/SeoSearchContext';

import { HighlightMarkersProvider } from '@spotahome/marketplace-common/src/context/HighlightMarkersContext';

import '@spotahome/seo-landings-common/src/scss/init.scss';

const SeoPage = () => {
  const { [SeoPage.NAME]: requestedData } = useSoyuzData();

  const {
    error,
    userId,
    initialCookie,
    modules,
    deviceType,
    slug,
    routing,
    propertyType,
    anchorText,
    seoSnippet,
    cityId,
    currentCity,
    initialFilters,
    initialHomecards,
    initialNMarkers
  } = requestedData;
  const isUserLogged = !!userId;

  useCoreWebVitals();
  usePageLoadTime();

  useEffect(() => {
    configureMetricsActions();

    if (cityId && slug) {
      Metrics.actions.sendActionEvent(Metrics.actions.VIEW_SEO_LANDING, {
        cityId,
        slug
      });
    }
  }, [cityId, slug]);

  if (error === 'notFound' || error === 'gone') {
    return (
      <NotFound
        initialCookie={initialCookie}
        deviceType={deviceType}
        error={error}
      />
    );
  }

  return (
    <CookiesProvider initialCookie={initialCookie}>
      <ThemeProvider flags={{ newButtons: true }}>
        <UserInfoProvider>
          <HotspotsProvider>
            <CitiesProvider skipAllCities>
              <DeviceProvider deviceType={deviceType}>
                <ToastContainer />
                {!userId && <GoogleOneTap />}
                <ZohoChatbot />
                <HighlightMarkersProvider>
                  <FiltersProvider
                    initialFilters={initialFilters}
                    cityId={currentCity.id}
                    shouldUpdateUrl={false}
                  >
                    <FacetSearchProvider
                      cityId={currentCity.id}
                      initialFilters={initialFilters}
                    >
                      <SeoSearchProvider
                        cityId={currentCity.id}
                        initialFilters={initialFilters}
                        initialHomecards={initialHomecards}
                        initialNMarkers={initialNMarkers}
                      >
                        <SeoLocalesContainer routing={routing}>
                          <SeoCitiesProvider
                            currentCity={currentCity}
                            propertyType={propertyType}
                            anchorText={anchorText}
                          >
                            <SeoPageWrapper
                              modules={modules}
                              loadModules={loadModules}
                              deviceType={deviceType}
                              seoSnippet={seoSnippet}
                            />
                          </SeoCitiesProvider>
                          {!isUserLogged && <GoogleOneTap />}
                          <OptInModalWrapper />
                          <LoginSignupModalWrapper />
                        </SeoLocalesContainer>
                      </SeoSearchProvider>
                    </FacetSearchProvider>
                  </FiltersProvider>
                </HighlightMarkersProvider>
              </DeviceProvider>
            </CitiesProvider>
          </HotspotsProvider>
        </UserInfoProvider>
      </ThemeProvider>
    </CookiesProvider>
  );
};

SeoPage.NAME = 'SeoPage';

export default SeoPage;
